import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'

import {
  Container,
  IconContainer,
  Label,
  LabelHint,
  LabelOptional,
  NumberIconContainer,
  TextInput,
  TextInputContainer,
} from './Input.styled'

interface Props {
  value?: string | number | readonly string[] | undefined
  className?: string
  required?: boolean
  disabled?: boolean
  label?: string
  placeholder?: string
  hint?: string
  error?: boolean
  pattern?: string
  regex?: RegExp
  type?: React.HTMLInputTypeAttribute
  onChange?: (value: string, target: HTMLInputElement) => void
  autoFocus?: boolean
  min?: string | number | undefined
  maxLength?: number
  step?: string | number | undefined
  readOnly?: boolean
  hideOptional?: boolean
  onBlur?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

export const Input = (props: Props): JSX.Element => {
  const {
    value,
    className,
    label,
    placeholder,
    hint,
    error,
    disabled,
    required,
    pattern,
    regex,
    type,
    onChange,
    autoFocus,
    min,
    maxLength,
    step,
    readOnly = false,
    hideOptional = false,
    onBlur,
    onKeyDown,
  } = props

  const [revealPassword, setRevealPassword] = useState<boolean>(false)

  const { t } = useTranslation()

  return (
    <Container className={className}>
      <Label>
        {label}
        {!required && !hideOptional && <LabelOptional>{` (${t('components_Input_label_optional')})`}</LabelOptional>}
      </Label>
      <TextInputContainer error={error} disabled={disabled}>
        {type == 'number' && <NumberIconContainer>#</NumberIconContainer>}
        <TextInput
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          step={step}
          disabled={disabled}
          required={required}
          pattern={pattern}
          type={revealPassword ? 'text' : type}
          min={min}
          maxLength={maxLength}
          placeholder={placeholder}
          value={value}
          readOnly={readOnly}
          onChange={(e) => {
            if (disabled || (regex && !regex.test(e.target.value))) {
              return
            }

            onChange?.(e.target.value, e.target)
          }}
          autoFocus={autoFocus}
        />
        {type == 'password' && (
          <IconContainer
            onClick={() => {
              setRevealPassword(!revealPassword)
            }}
          >
            {revealPassword ? (
              <VisibilityOffIcon style={{ fontSize: 16 }} />
            ) : (
              <VisibilityIcon style={{ fontSize: 16 }} />
            )}
          </IconContainer>
        )}
      </TextInputContainer>
      {hint && <LabelHint error={error}>{hint}</LabelHint>}
    </Container>
  )
}
